import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  LuSparkles,
  LuInfo,
  LuRefreshCw,
  LuFileText,
  LuTarget,
  LuClipboardPaste,
  LuUpload,
  LuLock,
} from "react-icons/lu";
import { Btn, Card, Note, Spinner, Field } from "../components/Ui";
import {
  SuggestionChunks,
  streamJsonLines,
  TAILORED_SUGGESTIONS_STREAM_API,
} from "./JobSearch";
import CustomResumeSheet from "./CustomResumeSheet";
import "./jobs.css"; // SuggestionChunks styling (.score-card / .chunk-card / .stream-row)
import "./jobMatch.css";

/* ---------------------------------------------------------------
   Match a Job — paste any job description, see how your resume
   stacks up (match %, skill gaps, domain fit, action items), then
   continue straight into tailoring the resume for that role.

   Reuses the exact engine the job-based flow uses:
     resume-update-suggestions-tailored-job-v4  (streamed gap analysis)
     <SuggestionChunks>                          (the gap renderer)
     <CustomResumeSheet>                         (the tailor wizard)
   --------------------------------------------------------------- */

/* Fold the optional title/company into the posting text so the analysis has
   the full context, mirroring how jobInfoText builds the board-job blob. */
function composeJobText({ title, company, jd }) {
  return [title, company ? `at ${company}` : "", jd]
    .filter((s) => s && s.trim())
    .join("\n\n")
    .trim();
}

export default function JobMatch() {
  const userEmail = useSelector((s) => s.auth?.auth?.email) || "";
  const { apiToken, fileResume, featuresToBlock = [], resumeJson } = useSelector((s) => s.profile);

  const isTailoredBlocked = featuresToBlock.includes("block_tailored");
  const isGenResumeBlocked = featuresToBlock.includes("block_genresume");
  const hasResume = Boolean(fileResume || resumeJson);

  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jd, setJd] = useState("");
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [needsResume, setNeedsResume] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const updatesRef = useRef([]);

  const synthJob = {
    job_title: title.trim(),
    employer_name: company.trim(),
    job_description: jd,
  };

  const analyze = async () => {
    if (!userEmail) {
      setError("We couldn't read your account. Please sign in again.");
      return;
    }
    // Guard against non-job / too-short input (Pavithra #1–#3): require a
    // meaningful amount of text before we run an analysis.
    const text = jd.trim();
    if (text.length < 60 || text.split(/\s+/).filter(Boolean).length < 12) {
      setError("Please paste a real job description (the role's responsibilities and requirements) so the match is accurate.");
      return;
    }
    setError("");
    setNeedsResume(false);
    updatesRef.current = [];
    setUpdates([]);
    setLoading(true);
    try {
      const response = await fetch(TAILORED_SUGGESTIONS_STREAM_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken || ""}`,
        },
        body: JSON.stringify({
          email_id: userEmail,
          job_description: composeJobText({ title, company, jd }),
        }),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        if (response.status === 404 || /resume/i.test(body)) {
          setNeedsResume(true);
          setLoading(false);
          return;
        }
        throw new Error(`Analyze failed (${response.status})`);
      }
      await streamJsonLines(response, (chunk) => {
        updatesRef.current = [...updatesRef.current, chunk];
        setUpdates(updatesRef.current);
      });
      setReady(true);
      setLoading(false);
    } catch (err) {
      console.error("Job match analyze failed:", err);
      setError("Something went wrong analyzing this job. Please try again in a moment.");
      setLoading(false);
    }
  };

  const reset = () => {
    setReady(false);
    setUpdates([]);
    updatesRef.current = [];
    setError("");
    setNeedsResume(false);
    // "Analyze another" must give a fresh, empty box (Pavithra #4)
    setJd("");
    setTitle("");
    setCompany("");
  };

  /* ---- gated / prerequisite states ---- */
  if (isTailoredBlocked) {
    return (
      <div className="jm-wrap stack-4">
        <Header />
        <Card className="jm-gate stack-3">
          <span className="jm-gate-ic"><LuLock size={22} /></span>
          <div className="stack-2">
            <h2 className="t-h3">Job match is a Pro feature</h2>
            <p className="t-sm">
              Upgrade to paste any job description and get an instant match score, skill-gap analysis,
              and a resume tailored to the role.
            </p>
          </div>
          <div>
            <Btn to="/subscription" className="btn-mint btn-pill">
              <LuSparkles size={15} /> See Pro plans
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="jm-wrap stack-4">
      <Header />

      {!hasResume ? (
        <Card className="jm-gate stack-3">
          <span className="jm-gate-ic"><LuUpload size={22} /></span>
          <div className="stack-2">
            <h2 className="t-h3">Upload your resume first</h2>
            <p className="t-sm">
              We compare the job description against your resume. Add your resume and you'll be ready to match
              any role in seconds.
            </p>
          </div>
          <div>
            <Btn to="/resume-upload" className="btn-mint btn-pill">
              <LuFileText size={15} /> Upload resume
            </Btn>
          </div>
        </Card>
      ) : !ready ? (
        <div className="jm-grid">
          <Card className="stack-3">
            <div className="grid-2" style={{ gap: 12 }}>
              <Field label="Job title (optional)">
                <input
                  className="input"
                  value={title}
                  placeholder="e.g. Senior Data Scientist"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label="Company (optional)">
                <input
                  className="input"
                  value={company}
                  placeholder="e.g. Acme Corp"
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Job description">
              <textarea
                className="input jm-textarea"
                rows={14}
                value={jd}
                placeholder="Paste the full job description or requirements here…"
                onChange={(e) => setJd(e.target.value)}
              />
            </Field>

            {needsResume ? (
              <Note tone="danger">
                <LuInfo size={16} />
                <span>
                  We couldn't find your parsed resume. Please{" "}
                  <Link to="/resume-upload" className="t-accent" style={{ fontWeight: 600 }}>
                    upload your resume
                  </Link>{" "}
                  and try again.
                </span>
              </Note>
            ) : null}
            {error ? (
              <Note tone="danger">
                <LuInfo size={16} />
                <span>{error}</span>
              </Note>
            ) : null}

            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span className="t-xs">{jd.trim().length} characters</span>
              <Btn
                className="btn-mint btn-pill"
                onClick={analyze}
                loading={loading}
                disabled={loading || jd.trim() === ""}
              >
                <LuSparkles size={15} /> Analyze match
              </Btn>
            </div>

            {loading ? (
              <div className="stream-row">
                <Spinner /> Analyzing your resume against this job…
              </div>
            ) : null}
          </Card>

          <Card className="jm-aside stack-3">
            <div>
              <div className="jm-aside-head">
                <span className="jm-aside-ic"><LuClipboardPaste size={16} /></span>
                <span className="t-label">How it works</span>
              </div>
              <ol className="jm-steps">
                <li><strong>Paste</strong> any job description or list of requirements.</li>
                <li><strong>See your gaps</strong> — score, missing skills, and next steps.</li>
                <li><strong>Tailor</strong> your resume to the role in one click.</li>
              </ol>
            </div>

            <div className="jm-divider" />

            <div>
              <div className="t-label" style={{ marginBottom: 8 }}>What you'll get</div>
              <ul className="jm-gets">
                <li>Overall match score &amp; submission verdict</li>
                <li>Critical &amp; recommended skill gaps</li>
                <li>Domain fit and transferable skills</li>
                <li>Resume bullet suggestions per role</li>
                <li>Immediate &amp; long-term action items</li>
              </ul>
            </div>

            <Note tone="info">
              <LuInfo size={16} />
              <span>Nothing is sent to the employer — this is a private analysis just for you.</span>
            </Note>
          </Card>
        </div>
      ) : (
        <div className="stack-4">
          <Card className="jm-result-head">
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="t-label">Analyzed against your resume</div>
              <div className="t-h3" style={{ marginTop: 2 }}>
                {title.trim() || "Pasted job description"}
                {company.trim() ? <span className="t-sm"> · {company.trim()}</span> : null}
              </div>
            </div>
            <div className="row" style={{ gap: 8, flexShrink: 0 }}>
              <Btn variant="ghost" className="btn-pill" onClick={reset}>
                <LuRefreshCw size={15} /> Analyze another
              </Btn>
              <Btn
                className="btn-mint btn-pill"
                onClick={() => (isGenResumeBlocked ? null : setStudioOpen(true))}
                to={isGenResumeBlocked ? "/subscription" : undefined}
              >
                <LuFileText size={15} /> Tailor my resume for this job
              </Btn>
            </div>
          </Card>

          {loading ? (
            <div className="stream-row">
              <Spinner /> Reviewing your resume…
            </div>
          ) : null}

          <SuggestionChunks updates={updates} />
        </div>
      )}

      <CustomResumeSheet
        open={studioOpen}
        onClose={() => setStudioOpen(false)}
        job={synthJob}
        updates={updates}
        resumeJson={resumeJson}
      />
    </div>
  );
}

function Header() {
  return (
    <div className="jm-head stack-2">
      <div className="jm-head-title">
        <span className="jm-head-ic"><LuTarget size={20} /></span>
        <h1 className="t-h1">Match a job</h1>
      </div>
      <p className="t-sm">
        Paste any job description to see how your resume measures up — match score, skill gaps, and a resume
        tailored to the role.
      </p>
    </div>
  );
}
